// ── Universal Production Tools Menu — centralized route resolver ──
//
// Single source of truth for how each of the nine Production Tools is
// reached. Every route below is copied from the tool's own existing,
// working navigation (Project Overview's own action list, each tool's
// own "Media Library" / "Back to Project" breadcrumb links) — nothing
// here is a new route. If a tool requires a project (Upload, Go Live,
// Images & Posts are nested under /workspace/project/:projectId/...
// with no project-less route registered in App.js), resolveToolRoute
// returns null so the dropdown can disable that entry instead of
// linking to a broken URL.
import {
  UploadCloud, Radio, Film, Scissors, Image, Sparkles, CalendarClock, LayoutTemplate, FolderOpen,
} from 'lucide-react';

export const PRODUCTION_TOOLS = [
  { id: 'upload', label: 'Upload', icon: UploadCloud },
  { id: 'live', label: 'Go Live', icon: Radio },
  { id: 'editor', label: 'Editor', icon: Film },
  { id: 'clips', label: 'Clips', icon: Scissors },
  { id: 'images-posts', label: 'Images & Posts', icon: Image },
  { id: 'optimize', label: 'Optimize', icon: Sparkles },
  { id: 'schedule', label: 'Schedule', icon: CalendarClock },
  { id: 'templates', label: 'Templates', icon: LayoutTemplate },
  { id: 'media-library', label: 'Media Library', icon: FolderOpen },
];

function qs(params) {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

/**
 * Resolve the URL for a Production Tool given the caller's current
 * context. Returns null when the tool cannot be opened with the given
 * context (e.g. Upload/Go Live/Images & Posts with no projectId) —
 * callers must treat null as "disable this entry", never fall back to
 * a guessed URL.
 *
 * ctx: { creatorId, projectId, assetId, returnTo }
 */
export function resolveToolRoute(toolId, ctx = {}) {
  const { creatorId, projectId, assetId, returnTo } = ctx;

  switch (toolId) {
    case 'upload':
      return projectId ? `/app/create/workspace/project/${projectId}/upload` : null;

    case 'live':
      return projectId ? `/app/create/workspace/project/${projectId}/live` : null;

    case 'images-posts':
      return projectId ? `/app/create/workspace/project/${projectId}/images-posts` : null;

    case 'editor':
      // A specific asset always wins — matches every existing "Edit"
      // action elsewhere in the app. With no asset, Editor still opens
      // (its own empty-state), just carrying projectId/returnTo as
      // query params the same way Project Overview's own editorBaseRoute
      // already does.
      if (assetId) return `/app/create/editor/${assetId}`;
      return `/app/create/editor${qs({ projectId, returnTo })}`;

    case 'clips':
      if (assetId) return `/app/create/clips/${assetId}`;
      return `/app/create/clips${qs({ projectId, returnTo })}`;

    case 'optimize':
      return `/app/create/optimize${qs({ assetId, projectId, creatorId })}`;

    case 'schedule':
      return `/app/create/schedule${qs({ projectId, creatorId })}`;

    case 'templates':
      return `/app/create/templates${qs({ projectId, creatorId, returnTo })}`;

    case 'media-library':
      return `/app/create/content-studio${qs({ projectId, returnTo })}`;

    default:
      return null;
  }
}

export function isToolAvailable(toolId, ctx) {
  return !!resolveToolRoute(toolId, ctx);
}
